import { useMemo } from "react";

export type CallPlaybackHandlers = {
  play: () => void;
  pause: () => void;
  seek: (seconds: number) => void;
};

export type CallPlayback = {
  /** The player claims the bus on mount and releases it (null) on unmount. */
  register: (handlers: CallPlaybackHandlers | null) => void;
  /** Transcript -> player. `play` also starts playback if it is paused. */
  seek: (seconds: number, options?: { play?: boolean }) => void;
  /** Player -> subscribers. null means "no position": ended, reset or inactive. */
  publish: (time: number | null) => void;
  subscribe: (listener: (time: number | null) => void) => () => void;
  getTime: () => number | null;
};

const createCallPlayback = (): CallPlayback => {
  let handlers: CallPlaybackHandlers | null = null;
  let time: number | null = null;
  const listeners = new Set<(time: number | null) => void>();

  const publish = (next: number | null) => {
    if (next === time) return;
    time = next;
    listeners.forEach((listener) => listener(next));
  };

  return {
    register: (next) => {
      handlers = next;
      // A player leaving takes its position with it, so anything highlighting
      // the current line clears instead of freezing on the last one.
      if (!next) publish(null);
    },
    seek: (seconds, options) => {
      if (!handlers) return;
      handlers.seek(seconds);
      if (options?.play) handlers.play();
    },
    publish,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getTime: () => time
  };
};

/**
 * Connects a recording player to the transcript rendered beside it (CRM-1330):
 * the player publishes its position, the transcript highlights the line being
 * spoken, and clicking a line seeks the player back.
 *
 * Deliberately imperative. The player writes its progress straight to the DOM
 * from a requestAnimationFrame loop, and routing that through React state would
 * re-render the whole card ~60 times a second. Subscribers take the raw stream
 * and re-render only when what they derive from it (the active line) changes.
 *
 * One bus per player/transcript pair — create it in the component that renders
 * both, and pass it down to each.
 */
export const useCallPlayback = (): CallPlayback =>
  useMemo(createCallPlayback, []);
